import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { resolveOptimisticOrder } from '@griply/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ID } from 'shared/types/id';
import { blurActiveElement } from 'shared/utils/blur-active-element';

type MinimalItem = {
  id: ID;
};

type Options<Item> = {
  items?: Item[];
  onDragEnd?: (ids: ID[]) => void;
};

export const useSortableItemsControls = <Item extends MinimalItem>({
  items,
  onDragEnd,
}: Options<Item> = {}) => {
  const sourceItems = useMemo(() => items ?? [], [items]);
  const [activeId, setActiveId] = useState<ID>();
  const [override, setOverride] = useState<Item[]>();

  const renderedItems = resolveOptimisticOrder(sourceItems, override);

  const activeItem = useMemo(
    () =>
      activeId ? renderedItems.find(({ id }) => id === activeId) : undefined,
    [activeId, renderedItems],
  );

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => setActiveId(active.id.toString()),
    [],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveId(undefined);

      blurActiveElement();

      if (!over?.id) {
        return;
      }

      const activeIndex = renderedItems.findIndex(({ id }) => id === active.id);
      const overIndex = renderedItems.findIndex(({ id }) => id === over.id);

      if (activeIndex < 0 || overIndex < 0) {
        return;
      }

      const reordered = arrayMove(renderedItems, activeIndex, overIndex);
      setOverride(reordered);
      onDragEnd?.(reordered.map(({ id }) => id));
    },
    [onDragEnd, renderedItems],
  );

  const handleDragCancel = useCallback(() => setActiveId(undefined), []);

  useEffect(() => {
    if (override && renderedItems !== override) {
      setOverride(undefined);
    }
  }, [override, renderedItems]);

  return {
    items: renderedItems,
    activeItem,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    // Lets a consumer force a transient render order (used to snap back to the
    // props order while a card is dragged outside the list).
    setItems: setOverride,
  };
};
